// Demo API Client - loads from static JSON files instead of real API

import { debug } from '@/utils/debug'
import type {
  Garden,
  Plant,
  Building,
  GroundShape,
  JsonApiResponse,
  JsonApiListResponse,
  JsonApiResource,
  Product,
  ProductFile,
  Point,
  Layer
} from '@/types'

const DEMO_BASE_PATH = '/demo'

class DemoApiClient {
  constructor() {
    debug.log('API', 'Demo API Client initialized (loading from static files)')
  }

  /**
   * Fetch a static JSON file from the demo folder
   */
  private async fetchJson<T>(path: string): Promise<T> {
    const url = `${DEMO_BASE_PATH}/${path}`
    debug.log('API', `[Demo] Fetching: ${url}`)

    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Demo file not found: ${path}`)
    }

    return response.json()
  }

  /**
   * Fetch list of available gardens (just the demo garden)
   */
  async getGardens(): Promise<{ id: string; name: string; thumbnailUrl?: string }[]> {
    debug.log('API', '[Demo] Returning demo garden')

    // Load garden metadata to get name and thumbnail
    const response = await this.fetchJson<JsonApiResponse>('garden.json')
    const attrs = response.data.attributes

    return [{
      id: 'demo',
      name: attrs['name'] as string || 'Demo Garden',
      thumbnailUrl: attrs['preview-thumbnail-url'] as string | undefined
    }]
  }

  /**
   * Fetch demo garden metadata
   */
  async getGarden(_id: string): Promise<Garden> {
    debug.log('API', '[Demo] Loading garden metadata...')

    const response = await this.fetchJson<JsonApiResponse>('garden.json')
    const garden = this.parseGardenMetadata(response)

    debug.success('API', `[Demo] Parsed garden: "${garden.name}"`)
    return garden
  }

  /**
   * Fetch demo garden objects with products and textures
   */
  async getGardenWithAssets(
    _gardenId: string,
    options: { textureKinds?: string[]; categoryLayerMap?: Map<string, Layer> } = {}
  ): Promise<{
    plants: Plant[]
    buildings: Building[]
    groundShapes: GroundShape[]
    products: Map<string, Product>
    modelUrls: Map<string, ProductFile[]>
    textureUrls: Map<string, ProductFile[]>
  }> {
    debug.log('API', '[Demo] Loading garden objects with assets...')

    const response = await this.fetchJson<JsonApiListResponse>('garden-objects.json')

    debug.log('API', `[Demo] Received ${response.data?.length || 0} garden objects, ${response.included?.length || 0} included resources`)

    // Parse included resources (products and textures)
    const { products, textures } = this.parseIncludedResources(response.included || [])

    // Use provided category map or create empty one
    const categoryLayerMap = options.categoryLayerMap || new Map<string, Layer>()
    if (!options.categoryLayerMap) {
      debug.warn('API', '[Demo] No categoryLayerMap provided, using geometry-based fallback')
    }

    // Parse garden objects
    const plants: Plant[] = []
    const buildings: Building[] = []
    const groundShapes: GroundShape[] = []

    for (const item of response.data || []) {
      const parsed = this.parseGardenObject(item, products, categoryLayerMap)
      if (parsed) {
        if (parsed.type === 'plant') {
          plants.push(parsed as Plant)
        } else if (parsed.type === 'building') {
          buildings.push(parsed as Building)
        } else if (['rectangle', 'polygon', 'ellipse', 'path'].includes(parsed.type)) {
          groundShapes.push(parsed as GroundShape)
        }
      }
    }

    // Organize textures by product and kind
    const modelUrls = new Map<string, ProductFile[]>()
    const textureUrls = new Map<string, ProductFile[]>()

    for (const [productId, productTextures] of textures) {
      const glbFiles = productTextures.filter(t => t.kind === 'glb')
      const topFiles = productTextures.filter(t => t.kind === 'top')

      if (glbFiles.length > 0) {
        modelUrls.set(productId, glbFiles)
      }
      if (topFiles.length > 0) {
        textureUrls.set(productId, topFiles)
      }
    }

    debug.success('API', `[Demo] Summary: ${plants.length} plants, ${buildings.length} buildings, ${groundShapes.length} ground shapes`)
    debug.success('API', `[Demo] Assets: ${modelUrls.size} glb, ${textureUrls.size} top`)

    return { plants, buildings, groundShapes, products, modelUrls, textureUrls }
  }

  /**
   * Fetch billboard texture for a specific product from demo files
   * Returns null if not found (triggers fallback to green cone)
   */
  async getBillboardTexture(
    productId: string,
    _options: { month?: number; size?: number; dpm?: number } = {}
  ): Promise<ProductFile | null> {
    debug.log('API', `[Demo] Fetching billboard texture for product ${productId}...`)

    try {
      const response = await this.fetchJson<JsonApiListResponse>(`billboards/${productId}.json`)

      if (!response.data || response.data.length === 0) {
        debug.log('API', `[Demo] No billboard texture found for product ${productId}`)
        return null
      }

      const item = response.data[0]
      const attrs = item.attributes
      const productRelation = item.relationships?.product?.data as { id: string } | undefined

      const file: ProductFile = {
        id: item.id,
        productId: productRelation?.id || productId,
        kind: 'billboard' as ProductFile['kind'],
        fileUrl: attrs['file-url'] as string,
        dpm: attrs['dpm'] as number | undefined,
        width: attrs['width'] as number | undefined,
        height: attrs['height'] as number | undefined
      }

      debug.success('API', `[Demo] Billboard texture found: ${file.fileUrl}`)
      return file
    } catch {
      // File not found - return null to trigger fallback
      debug.log('API', `[Demo] Billboard file not found for product ${productId}, using fallback`)
      return null
    }
  }

  /**
   * Fetch categories for all layers and build a lookup map
   */
  async getCategoryLayerMap(): Promise<Map<string, Layer>> {
    debug.log('API', '[Demo] Building category layer map...')

    const categoryMap = new Map<string, Layer>()

    // Load all 3 category files
    const layerFiles: { file: string; layer: Layer }[] = [
      { file: 'product-categories-plants.json', layer: 'plants' },
      { file: 'product-categories-buildings.json', layer: 'ground' },  // surfaces
      { file: 'product-categories-ground.json', layer: 'architecture' }  // architecture objects
    ]

    for (const { file, layer } of layerFiles) {
      try {
        const response = await this.fetchJson<JsonApiListResponse>(file)
        for (const item of response.data || []) {
          categoryMap.set(item.id, layer)
        }
        debug.log('API', `[Demo] Loaded ${response.data?.length || 0} categories for ${layer}`)
      } catch (error) {
        debug.warn('API', `[Demo] Failed to load ${file}`, error)
      }
    }

    debug.success('API', `[Demo] Category map built with ${categoryMap.size} categories`)
    return categoryMap
  }

  /**
   * Parse garden metadata from API response
   */
  private parseGardenMetadata(gardenResponse: JsonApiResponse): Garden {
    const data = gardenResponse.data
    const attrs = data.attributes

    // Parse scheme outline if present
    let schemeOutline = undefined
    const outlineGeometry = attrs['outline-geometry'] as string | null
    if (outlineGeometry && outlineGeometry.trim() !== '') {
      schemeOutline = this.parseOutlineGeometry(outlineGeometry)
    }

    return {
      id: data.id,
      token: attrs['token'] as string || '',
      name: attrs['name'] as string,
      description: attrs['description'] as string | undefined,
      rotation: (attrs['rotation'] as number) || 0,
      isEditable: false, // Demo mode is always read-only
      previewThumbnailUrl: attrs['preview-thumbnail-url'] as string | undefined,

      schemeImage: {
        imageUrl: attrs['background-image-url'] as string | undefined,
        x: (attrs['background-offset-x'] as number) || 0,
        y: (attrs['background-offset-y'] as number) || 0,
        scale: (attrs['background-scale'] as number) || 1
      },
      schemeOutline,

      properties: {},

      plants: [],
      buildings: [],
      groundShapes: []
    }
  }

  /**
   * Parse included resources from JSON:API response
   */
  private parseIncludedResources(included: JsonApiResource[]): {
    products: Map<string, Product>
    textures: Map<string, ProductFile[]>
  } {
    const products = new Map<string, Product>()
    const textures = new Map<string, ProductFile[]>()

    for (const item of included) {
      if (item.type === 'products') {
        const attrs = item.attributes
        const product: Product = {
          id: item.id,
          code: attrs['code'] as string || `ID-${item.id}`,
          name: attrs['name'] as string || 'Unknown',
          latinName: attrs['latin-name'] as string | undefined,
          mainCategoryId: attrs['main-category-id'] != null ? String(attrs['main-category-id']) : undefined,
          elevation: attrs['elevation'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined
        }
        products.set(item.id, product)
      } else if (item.type === 'textures') {
        const attrs = item.attributes
        const productRelation = item.relationships?.product?.data as { id: string } | undefined
        const productId = productRelation?.id || attrs['product-id'] as string

        if (!productId) continue

        const file: ProductFile = {
          id: item.id,
          productId,
          kind: attrs['kind'] as ProductFile['kind'],
          fileUrl: attrs['file-url'] as string,
          month: attrs['season'] as number | undefined,
          size: attrs['size'] as string | undefined,
          dpm: attrs['dpm'] as number | undefined,
          width: attrs['width'] as number | undefined,
          height: attrs['height'] as number | undefined
        }

        if (!textures.has(productId)) {
          textures.set(productId, [])
        }
        textures.get(productId)!.push(file)
      }
    }

    return { products, textures }
  }

  /**
   * Parse a garden object from the API response
   */
  private parseGardenObject(
    item: JsonApiResource,
    products: Map<string, Product>,
    categoryLayerMap: Map<string, Layer>
  ): Plant | Building | GroundShape | null {
    const attrs = item.attributes

    const productRelation = item.relationships?.product?.data as { id: string; type: string } | undefined
    const productId = productRelation?.id

    const geometry = attrs['geometry'] as string | null
    const hasGeometry = geometry && geometry.trim() !== '' && geometry !== '[]'

    // Get product for elevation fallback and layer detection
    const product = productId ? products.get(productId) : undefined

    // Use garden object elevation, falling back to product elevation
    // This is important for architecture items (planters, decks) that define elevation in the product
    const objectElevation = attrs['elevation'] as number | null
    const effectiveElevation = (objectElevation != null && objectElevation > 0)
      ? objectElevation
      : (product?.elevation ?? 0)

    const baseObject = {
      id: item.id,
      x: (attrs['x'] as number) || 0,
      y: (attrs['y'] as number) || 0,
      z: (attrs['z'] as number) || 0,
      rotation: (attrs['rotation'] as number) || 0,
      elevation: effectiveElevation,
      zIndex: (attrs['z-index'] as number) || 0,
      productId: productId,
      productName: product?.name,
      label: attrs['label'] as string | undefined
    }

    // Determine layer from product's main category
    let layer: Layer | undefined
    if (product?.mainCategoryId) {
      layer = categoryLayerMap.get(product.mainCategoryId)
    }

    // Fallback: use geometry presence to determine layer
    if (!layer) {
      layer = hasGeometry ? 'ground' : 'plants'
    }

    switch (layer) {
      case 'ground':
        return this.parseAsGroundShape(item, baseObject, geometry)
      case 'architecture':
        return this.parseAsBuilding(item, baseObject, product)
      case 'plants':
      default:
        return this.parseAsPlant(item, baseObject, product)
    }
  }

  private parseAsGroundShape(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    geometry: string | null
  ): GroundShape | null {
    if (geometry && geometry.trim() !== '' && geometry !== '[]') {
      const parsed = this.parseSvgGeometry(geometry)
      if (parsed) {
        return {
          ...baseObject,
          type: parsed.type,
          width: parsed.width,
          height: parsed.height,
          points: parsed.points,
          textureUrl: undefined
        } as GroundShape
      }
    }

    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'rectangle',
      width: (attrs['width'] as number) || 100,
      height: (attrs['height'] as number) || 100,
      textureUrl: undefined
    } as GroundShape
  }

  private parseAsBuilding(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    product?: Product
  ): Building {
    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'building',
      width: (attrs['width'] as number) || product?.width || 100,
      height: (attrs['height'] as number) || product?.height || 100
    } as Building
  }

  private parseAsPlant(
    item: JsonApiResource,
    baseObject: Omit<Plant, 'type' | 'planted' | 'width' | 'height'>,
    product?: Product
  ): Plant {
    const attrs = item.attributes
    return {
      ...baseObject,
      type: 'plant',
      width: (attrs['width'] as number) || product?.width || 50,
      height: (attrs['height'] as number) || product?.height || 50,
      planted: false
    } as Plant
  }

  /**
   * Stub — demo mode does not create gardens on server
   */
  async createGarden(_attrs: { name: string; description?: string }): Promise<{ id: string; name: string }> {
    debug.log('API', '[Demo] createGarden is a no-op in demo mode')
    return { id: 'demo-new', name: _attrs.name }
  }

  /**
   * Stub — demo mode does not persist garden properties
   */
  async updateGarden(_id: string, _properties: Record<string, unknown>, _gardenAttributes?: Record<string, unknown>): Promise<void> {
    debug.log('API', '[Demo] updateGarden is a no-op in demo mode')
  }

  async getTextures(
    _productIds: string[],
    _options?: { kinds?: string[] }
  ): Promise<Map<string, ProductFile[]>> {
    debug.log('API', '[Demo] getTextures not available in demo mode')
    return new Map()
  }

  private parseSvgGeometry(geometry: string): {
    type: 'rectangle' | 'polygon' | 'ellipse'
    width: number
    height: number
    points?: Point[]
  } | null {
    const decoded = geometry
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")

    // Rectangle
    const rectMatch = decoded.match(/<rect[^>]*width=['"]([\d.]+)['"][^>]*height=['"]([\d.]+)['"]/)
    if (rectMatch) {
      return { type: 'rectangle', width: parseFloat(rectMatch[1]), height: parseFloat(rectMatch[2]) }
    }

    const rectMatch2 = decoded.match(/<rect[^>]*height=['"]([\d.]+)['"][^>]*width=['"]([\d.]+)['"]/)
    if (rectMatch2) {
      return { type: 'rectangle', width: parseFloat(rectMatch2[2]), height: parseFloat(rectMatch2[1]) }
    }

    // Ellipse
    const ellipseMatch = decoded.match(/<ellips[ie]s?[^>]*width=['"]([\d.]+)['"][^>]*height=['"]([\d.]+)['"]/)
    if (ellipseMatch) {
      return { type: 'ellipse', width: parseFloat(ellipseMatch[1]), height: parseFloat(ellipseMatch[2]) }
    }

    // Polygon
    const polygonMatch = decoded.match(/<polygon[^>]*points=['"]([^'"]+)['"]/)
    if (polygonMatch) {
      const pointsStr = polygonMatch[1]
      const points: Point[] = pointsStr.split(/\s+/).map(pair => {
        const [x, y] = pair.split(',').map(parseFloat)
        return { x: x || 0, y: y || 0 }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      if (points.length >= 3) {
        const xs = points.map(p => p.x)
        const ys = points.map(p => p.y)
        const width = Math.max(...xs) - Math.min(...xs)
        const height = Math.max(...ys) - Math.min(...ys)

        return { type: 'polygon', width: width || 50, height: height || 50, points }
      }
    }

    return null
  }

  private parseOutlineGeometry(svg: string): { x: number; y: number; points: Point[] } | undefined {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(svg, 'text/xml')
      const polygon = doc.querySelector('polygon')
      if (!polygon) return undefined

      const x = parseFloat(polygon.getAttribute('data-x') || '0')
      const y = parseFloat(polygon.getAttribute('data-y') || '0')
      const pointsStr = polygon.getAttribute('points') || ''

      const points: Point[] = pointsStr.split(' ').map(pair => {
        const [px, py] = pair.split(',').map(parseFloat)
        return { x: px, y: py }
      }).filter(p => !isNaN(p.x) && !isNaN(p.y))

      return { x, y, points }
    } catch {
      return undefined
    }
  }
}

// Singleton instance
export const demoApi = new DemoApiClient()
